import { useId, useState } from "react";
import { useT } from "../../i18n";
import { Icons } from "../icons";

// Tags as chips with autocomplete from the vault's existing tags, replacing the single
// comma-separated text box (which made a typo silently create a new tag).
export function TagInput({
  value,
  known,
  onChange,
}: {
  value: string[];
  known: string[];
  onChange: (tags: string[]) => void;
}) {
  const t = useT();
  const listId = useId();
  const [text, setText] = useState("");

  function commit(raw: string) {
    const tag = raw.trim().replace(/,$/, "").trim();
    if (!tag || value.includes(tag)) {
      setText("");
      return;
    }
    onChange([...value, tag]);
    setText("");
  }

  return (
    <div className="flex flex-wrap items-center gap-1 rounded border border-border bg-panel2 px-1.5 py-1 focus-within:border-accent">
      {value.map((tag) => (
        <span key={tag} className="flex items-center gap-1 rounded bg-elevated px-1.5 py-0.5 text-[11px]">
          {tag}
          <button
            type="button"
            onClick={() => onChange(value.filter((x) => x !== tag))}
            aria-label={`${t("Remove")} ${tag}`}
            className="text-muted hover:text-danger"
          >
            <Icons.close className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        value={text}
        list={listId}
        onChange={(e) => {
          // A pasted "a, b, c" commits every complete tag and leaves the tail being typed.
          const v = e.target.value;
          if (v.includes(",")) {
            const parts = v.split(",");
            for (const p of parts.slice(0, -1)) commit(p);
            setText(parts[parts.length - 1].trimStart());
          } else {
            setText(v);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(text);
          } else if (e.key === "Backspace" && !text && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => commit(text)}
        placeholder={value.length ? "" : t("add a tag…")}
        aria-label={t("Tags")}
        className="min-w-24 flex-1 bg-transparent px-1 py-0.5 outline-none"
      />
      <datalist id={listId}>
        {known.filter((k) => !value.includes(k)).map((k) => (
          <option key={k} value={k} />
        ))}
      </datalist>
    </div>
  );
}
